import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useOverlay } from './overlayContext';

export interface OverlayHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** Extra controls before the close button. */
  actions?: ReactNode;
  /** Defaults to the enclosing overlay's close. Pass `null` to hide the button. */
  onClose?: (() => void) | null;
  closeLabel?: string;
}

/**
 * Title, optional subtitle and icon, header actions and the close button.
 * Drawers get a primary-coloured bar with contrast text; dialogs stay neutral.
 */
export function OverlayHeader({
  title,
  subtitle,
  icon,
  actions,
  onClose,
  closeLabel = 'Close',
}: OverlayHeaderProps) {
  const overlay = useOverlay();
  const close = onClose === undefined ? (overlay ? () => overlay.requestClose('closeButton') : undefined) : onClose;
  const filled = overlay?.variant === 'drawer';

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: 'center',
        px: { xs: 1, sm: 1.5 },
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0,
        ...(filled && {
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderColor: 'primary.main',
          '& .MuiIconButton-root': { color: 'inherit' },
        }),
      }}
    >
      {icon ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: filled ? 'rgba(255, 255, 255, 0.16)' : 'action.hover',
            color: filled ? 'inherit' : 'primary.main',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      ) : null}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography id={overlay?.titleId} variant='body1'  noWrap>
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            id={overlay?.subtitleId}
            variant="body2"
            noWrap
            sx={filled ? { color: 'inherit', opacity: 0.85 } : { color: 'text.secondary' }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      {actions}

      {close ? (
        <IconButton
          aria-label={closeLabel}
          onClick={close}
          disabled={overlay?.busy}
          size="small"
          edge="end"
          sx={filled ? { color: 'inherit', '&.Mui-disabled': { color: 'inherit', opacity: 0.5 } } : undefined}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      ) : null}
    </Stack>
  );
}
